import { ProjectElementNodeWithMetadata } from 'api/graphQL/types/projectElement.types'
import {
    getDialogUrl,
    updatePersistentConfigs,
    WORKSPACE_PRODUCT_LISTING_KEY,
    WorkspaceProductListing,
} from 'shared/utils/workspaceUtils'

export interface ProjectElementNodeExtended
    extends ProjectElementNodeWithMetadata {
    parentElementId?: string
}

export const getAssemblyPath = (
    node: ProjectElementNodeExtended | null,
    elementType: string | undefined
) => {
    if (!node) return 'dummy'

    switch (elementType) {
        case 'ASSEMBLY':
            return node.treePath || 'dummy'
        case 'PRODUCTFAMILY':
            return node.parentTreePath || 'dummy'
        case 'PRODUCTTYPE':
            return (
                node.parentTreePath?.split('||').slice(0, -1).join('||') ||
                'dummy'
            )
        default:
            return 'dummy'
    }
}

export const extractFamilyFromPath = (parentTreePath: string | undefined) => {
    if (!parentTreePath) return ''
    const pathParts = parentTreePath.split('||')
    const familyPart = pathParts[pathParts.length - 1]
    return familyPart ? familyPart.replace(/\|/g, ' - ') : ''
}

export const extractNodeData = (
    node: ProjectElementNodeExtended | null | undefined,
    elementType: string | undefined
) => {
    if (!node)
        return {
            family: '',
            productType: '',
            familyId: '',
            parentNodeId: '',
        }

    // Note: parentTreePath, parentElementId, parentNodeId should be added to interface
    const nodeWithExtras = node as ProjectElementNodeWithMetadata & {
        parentTreePath?: string
        parentElementId?: string
        parentNodeId?: string
    }

    if (elementType === 'PRODUCTTYPE') {
        return {
            family: extractFamilyFromPath(nodeWithExtras.parentTreePath),
            productType: node.text?.replace(/\|/g, ' - ') || '',
            familyId: nodeWithExtras.parentElementId || '',
            parentNodeId: nodeWithExtras.parentNodeId || '',
        }
    } else if (elementType === 'PRODUCTFAMILY') {
        return {
            family: node.text?.replace(/\|/g, ' - ') || '',
            productType: '',
            familyId: node.elementId || '',
            parentNodeId: nodeWithExtras.parentNodeId || '',
        }
    }

    return {
        family: '',
        productType: '',
        familyId: '',
        parentNodeId: '',
    }
}

export const persistDialogState = (
    nodeData: ReturnType<typeof extractNodeData>,
    node: ProjectElementNodeExtended | null | undefined,
    assemblyPath: string,
    userId: string | undefined,
    projectId: string | undefined
) => {
    if (!userId || !projectId) return

    const persistentConfigs: Array<[string, any]> = [
        ['selectedFamily', nodeData.family],
        ['selectedFamilyId', nodeData.familyId],
        ['selectedProductType', nodeData.productType],
        ['selectedParentNodeId', nodeData.parentNodeId],
        ['selectedElementId', node?.elementId || ''],
        ['selectedElementType', node?.elementType || ''],
        ['selectedNodeId', node?.id || ''],
        ['isLastSubAssembly', false],
        ['selectedAssemblyPath', assemblyPath],
        ['propertyOption', null],
        ['divisionNumber', ''],
        ['selectedFilterType', ''],
        ['categoryNodeId', ''],
    ]

    persistentConfigs.forEach(([key, value]) => {
        updatePersistentConfigs(userId, projectId, key, value)
    })
}

export const sendServiceWorkerMessage = (
    data: Partial<WorkspaceProductListing>
) => {
    setTimeout(() => {
        if (navigator.serviceWorker?.controller) {
            navigator.serviceWorker.controller.postMessage({
                [WORKSPACE_PRODUCT_LISTING_KEY]: data,
            })
        }
    }, 500)
}

export const openProductDialog = (
    workspaceProductListing: Partial<WorkspaceProductListing>,
    projectId: string | undefined
) => {
    const url = getDialogUrl(workspaceProductListing, 'dialog')
    window.open(
        url,
        `ProductListing${projectId}`,
        'directories=no, titlebar=no, location=no, toolbar=no, menubar=no, resizable=yes, width=1300, height=1000'
    )

    sendServiceWorkerMessage(workspaceProductListing)
}

export const handleViewProducts = (
    node: ProjectElementNodeExtended | null | undefined,
    userId: string,
    projectId = ''
) => {
    const nodeData = extractNodeData(node || null, node?.elementType)
    const assemblyPath = getAssemblyPath(node || null, node?.elementType)

    persistDialogState(nodeData, node || null, assemblyPath, userId, projectId)

    const nodeWithLastSubAssembly = node as ProjectElementNodeExtended & {
        isLastSubAssembly?: boolean
    }
    const isLastSubAssembly =
        nodeWithLastSubAssembly?.isLastSubAssembly ?? false

    const workspaceProductListing: Partial<WorkspaceProductListing> = {
        projectId: projectId || '',
        elementNode: node || undefined,
        nodeId: node?.id || '',
        elementId: node?.elementId || '',
        elementType: node?.elementType || '',
        assemblyPath,
        updateBODList: true,
        isLastSubAssembly,
        ...nodeData,
        specAgentIdPt: '',
        divisionNumber: '',
        selectedFilterType: '',
        categoryNodeId: '',
    }

    openProductDialog(workspaceProductListing, projectId)
}

// Enhanced version for Workspace2ActionBar with elementItemValue support
export const handleViewProductsWithElementItem = (
    node: ProjectElementNodeExtended | null | undefined,
    elementItemValue: any,
    userId: string,
    projectId: string
) => {
    const nodeData = extractNodeData(node || null, node?.elementType)
    const assemblyPath = getAssemblyPath(node || null, node?.elementType)

    persistDialogState(nodeData, node || null, assemblyPath, userId, projectId)

    const nodeWithLastSubAssembly = node as ProjectElementNodeExtended & {
        isLastSubAssembly?: boolean
    }
    const isLastSubAssembly =
        nodeWithLastSubAssembly?.isLastSubAssembly ?? false

    const workspaceProductListing: Partial<WorkspaceProductListing> = {
        projectId: projectId || '',
        elementNode: node || undefined,
        nodeId: elementItemValue?.id || node?.id || '',
        elementId: elementItemValue?.elementId || node?.elementId || '',
        elementType: node?.elementType || '',
        assemblyPath,
        updateBODList: true,
        isLastSubAssembly,
        ...nodeData,
        specAgentIdPt: '',
        divisionNumber: '',
        selectedFilterType: '',
        categoryNodeId: '',
        baseElementId: node?.baseElementId || '',
        treePath: node?.treePath || '',
        isInProject: node?.isInProject || false,
    }

    openProductDialog(workspaceProductListing, projectId)
}
